/**
 * شاشة «لوحة المدير» (design/Director.dc.html).
 *
 * 🔴 الشاشة دي كانت `UnderConstruction` والعميل طالبها بالحرف: «التقارير اللي
 *    بتروح لمدير الأكاديمية من عمل مهامه». الأرقام كلها كانت موجودة في
 *    السيستم — بس مفيش حد بيجمّعها، فالمدير بيسأل الفريق بدل ما يشوف.
 *
 * القواعد المنفَّذة هنا:
 *   • 🔑 **كل رقم قابل للنقر ويوصّل لقايمته.** الرقم اللي مالوش قايمة
 *     بيتحوّل لديكور والمدير بيبطّل يثق فيه؛ عشان كده كل عدّاد هنا زرار
 *     بيفتح نفس الصفوف اللي العدّاد عدّها.
 *   • ⛔ **ممنوع رقم مخترع**: متوسط الإقفال بيرجع `null` لما العيّنة صغيرة
 *     ⇒ بيتعرض «—» مش صفر.
 *   • ⛔ **فعل أساسي كحلي واحد** في الشاشة: أخطر طابور يتيم.
 *   • ⛔ الحالة = لون واحد + نقطة واحدة، وأرقام لاتينية بس.
 *   • ⚠️ **بيجهّز مش بيبعت** (ملحق القرارات §2): أي بند بيخاطب ولي الأمر
 *     بيتوسم «بيجهّز الرسالة» — الأتمتة الحقيقية جوّه السيستم بس.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Shell } from "@/components/Shell";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  NoPermissionState,
  OfflineState,
} from "@/components/states/ScreenStates";
import { fmtNum } from "@/lib/format";
import { ApiError, OfflineError, errorMessageAr } from "@/api/client";
import { useRouter } from "@/lib/router";
import {
  DRILL_TITLES_AR,
  fetchDrill,
  fetchOverview,
  type DeptPulse,
  type DrillKind,
  type DrillPage,
  type Overview,
} from "@/api/director";
import {
  fetchChangeRequests,
  setChangeRequestStatus,
  type ChangeRequest,
  type ChangeRequestStatus,
} from "@/api/assistant";

const PER_PAGE = 8;

type PageState =
  | { kind: "loading" }
  | { kind: "ready"; value: Overview }
  | { kind: "forbidden"; messageAr: string }
  | { kind: "error"; messageAr: string }
  | { kind: "offline" };

type DrillState =
  | { kind: "idle" }
  | { kind: "loading"; drill: DrillKind; dept: string | null }
  | { kind: "ready"; value: DrillPage }
  | { kind: "error"; drill: DrillKind; messageAr: string };

/** «—» مش «0». الفرق بين «قِسنا ولقينا صفر» و«ما عندناش رقم». */
function dash(v: number | null): string {
  return v === null ? "—" : fmtNum(v);
}

export function Director() {
  const { navigate, param } = useRouter();
  const [state, setState] = useState<PageState>({ kind: "loading" });
  const [drill, setDrill] = useState<DrillState>({ kind: "idle" });
  const [page, setPage] = useState(1);
  const active = useRef<{ kind: DrillKind; dept: string | null } | null>(null);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      setState({ kind: "ready", value: await fetchOverview() });
    } catch (err) {
      // ⛔ ممنوع الفشل الصامت — السبب بيتسجّل وبيتعرض
      console.error("[director] تحميل اللوحة فشل", err);
      if (err instanceof OfflineError) setState({ kind: "offline" });
      else if (err instanceof ApiError && err.status === 403)
        setState({ kind: "forbidden", messageAr: err.messageAr });
      else setState({ kind: "error", messageAr: errorMessageAr(err) });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openDrill = useCallback(
    async (kind: DrillKind, dept: string | null, toPage = 1) => {
      active.current = { kind, dept };
      setPage(toPage);
      setDrill({ kind: "loading", drill: kind, dept });
      try {
        const value = await fetchDrill(kind, { dept, page: toPage, perPage: PER_PAGE });
        // سباق: المدير دعس على رقم تاني وإحنا بنحمّل — الرد القديم بيتلغى
        if (active.current?.kind !== kind || active.current?.dept !== dept) return;
        setDrill({ kind: "ready", value });
      } catch (err) {
        console.error(`[director] قايمة «${kind}» فشلت`, err);
        setDrill({ kind: "error", drill: kind, messageAr: errorMessageAr(err) });
      }
    },
    [],
  );

  /** وجهة الإشعار: `/director?panel=orphan_queues&dept=…` بتفتح القايمة فورًا */
  const panel = param("panel");
  const panelDept = param("dept");
  useEffect(() => {
    if (panel === "orphan_queues" && state.kind === "ready" && drill.kind === "idle") {
      void openDrill("orphan_tasks", panelDept);
    }
  }, [panel, panelDept, state.kind, drill.kind, openDrill]);

  const value = state.kind === "ready" ? state.value : null;
  const totals = value?.totals ?? null;

  // ⛔ أخطر بند في اللوحة كلها — وهو الفعل الكحلي الوحيد
  const worstOrphan = value?.orphan_queues[0] ?? null;

  const crumb = useMemo(() => {
    if (!totals) return <>اللوحة بتتحمّل…</>;
    return (
      <>
        <span className="num">{fmtNum(totals.needs_review)}</span> محتاج مراجعتك ·{" "}
        <span className="num">{fmtNum(totals.overdue)}</span> متأخر ·{" "}
        <span className="num">{fmtNum(totals.orphan_tasks)}</span> في طوابير مفيهاش حد
      </>
    );
  }, [totals]);

  return (
    <Shell title="لوحة المدير" crumb={crumb}>
      {state.kind === "loading" ? <LoadingState rows={6} /> : null}
      {state.kind === "offline" ? <OfflineState onRetry={() => void load()} fill /> : null}
      {state.kind === "forbidden" ? (
        <NoPermissionState
          messageAr={state.messageAr}
          onBack={() => navigate("/desk")}
          fill
        />
      ) : null}
      {state.kind === "error" ? (
        <ErrorState messageAr={state.messageAr} onRetry={() => void load()} />
      ) : null}

      {value ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
          {/* ═══ (1) الطوابير اللي محدش فيها — أخطر عطل تشغيلي ═══ */}
          <OrphanQueues
            value={value}
            worst={worstOrphan}
            onOpen={(dept) => void openDrill("orphan_tasks", dept)}
          />

          {/* ═══ (2) المحتاج مراجعتك — تلات بنود صريحة مش رقم غامض ═══ */}
          <div className="card" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
            <div className="sec-h">
              <span className="sec-title">
                محتاج مراجعتك (<span className="num">{fmtNum(value.needs_review.total)}</span>)
              </span>
              <span className="sec-note">كل بند بيفتح قايمته</span>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Counter
                label="حسابات مستنية اعتماد"
                n={value.needs_review.pending_accounts}
                onClick={() => void openDrill("pending_accounts", null)}
              />
              <Counter
                label="مهام مفتوحة عند الإدارة"
                n={value.needs_review.management_tasks}
                onClick={() => void openDrill("management_tasks", null)}
              />
              <Counter
                label="قرارات ما فتحتهاش"
                n={value.needs_review.open_decisions}
                onClick={() => void openDrill("open_decisions", null)}
              />
            </div>

            {/* 🔴 **مش عدّاد — قايمة بالاسم وبالحل.**
                صاحب الأكاديمية فتح السيستم ولقاه «فاضي خالص» وافتكره بايظ،
                وقعد هو والفريق ساعة يجرّبوا. الحساب كان محتاج سطر إعدادات.
                رقم مجرّد هنا كان هيتحوّل لديكور — الاسم والحل هما اللي
                بيخلّوا المدير يتصرّف في ثانية. */}
            {value.needs_review.misconfigured_accounts.length > 0 ? (
              <div
                className="card"
                style={{ padding: 14, display: "flex", flexDirection: "column",
                         gap: 8, borderRight: "3px solid var(--danger)" }}
              >
                <span className="sec-title">حسابات شغّالة ومش شايفة حاجة</span>
                {value.needs_review.misconfigured_accounts.map((a) => (
                  <div key={a.user_id} style={{ display: "flex", flexDirection: "column" }}>
                    <span className="t-body">{a.name_ar}</span>
                    <span className="t-meta">{a.problem_ar}</span>
                  </div>
                ))}
                <span className="t-meta">
                  التظبيط من «المستخدمون والصلاحيات» — عمود الدور.
                </span>
              </div>
            ) : null}
          </div>

          {/* ═══ (3) نبض الفرق ═══ */}
          <DeptPulseTable
            rows={value.departments}
            windowDays={value.thresholds.close_time_window_days}
            minSample={value.thresholds.min_sample_for_average}
            onOpen={(kind, dept) => void openDrill(kind, dept)}
          />

          {/* ═══ (4) الرحلات الواقفة ═══ */}
          <StalledJourneys
            value={value}
            onOpen={() => void openDrill("stalled_journeys", null)}
          />

          {/* ═══ (4ب) طلبات التعديل اللي الفريق قالها للمساعد ═══ */}
          <ChangeRequests open={panel === "change_requests"} />

          {/* ═══ (5) القايمة ورا الرقم ═══ */}
          <DrillPanel
            state={drill}
            page={page}
            onClose={() => {
              active.current = null;
              setDrill({ kind: "idle" });
            }}
            onPage={(p) => {
              const cur = active.current;
              if (cur) void openDrill(cur.kind, cur.dept, p);
            }}
          />
        </div>
      ) : null}
    </Shell>
  );
}

/* ══════════════════ طلبات التعديل من الفريق ══════════════════ */

/**
 * 🔑 الفريق بيقول للمساعد «الزرار ده ناقص» ⇒ المساعد بيتحقق ويسجّل ⇒ القايمة دي.
 *    المدير والمطوّر بيقروها، والتعديل بيتعمل بالكود، وأول ما الحالة تتغيّر
 *    اللي طلب بيتبلّغ — عشان محدش يحس إن كلامه راح في الهوا.
 * ⛔ مفيش «تنفيذ من هنا»: دي قايمة قرارات، مش شاشة تعديل سيستم.
 */
function ChangeRequests({ open }: { open: boolean }) {
  const [rows, setRows] = useState<ChangeRequest[] | null>(null);
  const [errorAr, setErrorAr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(open);
  const [busy, setBusy] = useState<string | null>(null);
  const [reply, setReply] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    try {
      setRows(await fetchChangeRequests("all"));
      setErrorAr(null);
    } catch (err) {
      console.error("[director] طلبات التعديل فشلت", err);
      setErrorAr(errorMessageAr(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function setStatus(id: string, status: ChangeRequestStatus) {
    if (busy) return;
    setBusy(id);
    try {
      const updated = await setChangeRequestStatus(id, status, reply[id]?.trim() || null);
      setRows((cur) => (cur ?? []).map((r) => (r.id === id ? updated : r)));
    } catch (err) {
      console.error("[director] تغيير حالة طلب التعديل فشل", err);
      setErrorAr(errorMessageAr(err));
    } finally {
      setBusy(null);
    }
  }

  const openOnes = (rows ?? []).filter((r) => r.status === "new" || r.status === "planned");
  const closedOnes = (rows ?? []).filter((r) => r.status === "done" || r.status === "rejected");
  const shown = expanded ? [...openOnes, ...closedOnes] : openOnes;

  return (
    <div className="card" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
      <div className="sec-h">
        <span className="sec-title">
          طلبات تعديل من الفريق (<span className="num">{fmtNum(openOnes.length)}</span> مفتوح)
        </span>
        <span className="sec-note">اللي الفريق قاله للمساعد عن السيستم نفسه — بيتعمل بالكود وبيتبلّغ صاحبه</span>
      </div>
      {errorAr ? <span className="t-meta" style={{ color: "var(--danger)" }}>{errorAr}</span> : null}
      {rows === null && !errorAr ? <span className="t-meta">بتتحمّل…</span> : null}
      {rows !== null && shown.length === 0 ? (
        <span className="t-meta">مفيش طلبات مفتوحة. لما حد يقول للمساعد إن حاجة ناقصة أو مش واضحة، بتظهر هنا.</span>
      ) : null}
      {shown.map((r) => (
        <div
          key={r.id}
          style={{ display: "flex", flexDirection: "column", gap: 6, padding: "10px 0",
                   borderTop: "1px solid var(--line-2)" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span className="t-body" style={{ fontWeight: 500 }}><bdi>{r.title_ar}</bdi></span>
            <span className="chip"><span className="bullet" />{r.status_label_ar}</span>
            <span className="t-meta">
              {r.requested_by.name_ar}{r.dept.length ? ` · ${r.dept.join("، ")}` : ""}
            </span>
          </div>
          <div className="t-meta" style={{ whiteSpace: "pre-wrap" }}>{r.details_ar}</div>
          {r.reply_ar ? <div className="t-meta">الرد: {r.reply_ar}</div> : null}
          {r.status === "new" || r.status === "planned" ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input
                className="reg-input"
                style={{ flex: "1 1 240px" }}
                placeholder="ردّ قصير يوصل للي طلب (اختياري)"
                value={reply[r.id] ?? ""}
                maxLength={1000}
                onChange={(e) => setReply((cur) => ({ ...cur, [r.id]: e.target.value }))}
              />
              {r.status === "new" ? (
                <button type="button" className="btn sm ghost" disabled={busy !== null}
                        onClick={() => void setStatus(r.id, "planned")}>هيتعمل</button>
              ) : null}
              <button type="button" className="btn sm primary" disabled={busy !== null}
                      onClick={() => void setStatus(r.id, "done")}>اتعمل</button>
              <button type="button" className="btn sm ghost" disabled={busy !== null}
                      onClick={() => void setStatus(r.id, "rejected")}>مش هيتعمل</button>
            </div>
          ) : null}
        </div>
      ))}
      {closedOnes.length > 0 ? (
        <button type="button" className="btn sm ghost" style={{ alignSelf: "flex-start" }}
                onClick={() => setExpanded((v) => !v)}>
          {expanded ? "اخفي اللي اتقفل" : `اعرض اللي اتقفل (${fmtNum(closedOnes.length)})`}
        </button>
      ) : null}
    </div>
  );
}

/* ══════════════════ الطوابير اليتيمة ══════════════════ */

function OrphanQueues({
  value,
  worst,
  onOpen,
}: {
  value: Overview;
  worst: Overview["orphan_queues"][number] | null;
  onOpen: (dept: string) => void;
}) {
  const rows = value.orphan_queues;
  return (
    <div className="card" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
      <div className="sec-h">
        <span className="sec-title">
          طوابير مفيهاش حد يستلم (<span className="num">{fmtNum(rows.length)}</span>)
        </span>
        <span className="sec-note">
          الطلب اللي داخل على قسم فاضي مش بيتنده عليه حد
        </span>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          title="مفيش طابور بلا مستلم"
          hint={`كل قسم فيه طلبات مستنية أكتر من ${fmtNum(
            value.thresholds.orphan_queue_minutes,
          )} دقيقة فيه موظف نشط`}
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {rows.map((q, i) => (
            <div
              key={q.dept}
              className="row-link"
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) auto",
                alignItems: "center",
                gap: 12,
                ...(i > 0 ? { borderTop: "1px solid var(--line)", paddingTop: 12 } : {}),
              }}
            >
              <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                  <span className="t-body">{q.dept_ar}</span>
                  {/* الحالة = لون واحد + نقطة واحدة */}
                  <span className="chip late">
                    <span className="bullet" />
                    <span className="num">{fmtNum(q.count)}</span> بلا صاحب
                  </span>
                </div>
                <span className="t-desc clip">
                  أقدم طلب مستني {fmtWait(q.waiting_minutes)} — «{q.sample_title_ar}»
                </span>
              </div>
              <button
                type="button"
                className={`btn sm ${worst && q.dept === worst.dept ? "primary" : "ghost"}`}
                onClick={() => onOpen(q.dept)}
              >
                افتح الطلبات
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** نص انتظار عربي بأرقام لاتينية — مطابق لـ`services/tasks.py::span_minutes_ar` */
function fmtWait(minutes: number): string {
  if (minutes < 60) return `${fmtNum(minutes)} دقيقة`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${fmtNum(hours)} ساعة`;
  return `${fmtNum(Math.floor(hours / 24))} يوم`;
}

/* ══════════════════ نبض الفرق ══════════════════ */

function DeptPulseTable({
  rows,
  windowDays,
  minSample,
  onOpen,
}: {
  rows: DeptPulse[];
  windowDays: number;
  minSample: number;
  onOpen: (kind: DrillKind, dept: string) => void;
}) {
  return (
    <div className="card" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
      <div className="sec-h">
        <span className="sec-title">نبض الفرق</span>
        <span className="sec-note">الأرقام كلها مهام · كل رقم بيفتح قايمته</span>
      </div>

      {rows.length === 0 ? (
        <EmptyState title="مفيش شغل مسجّل لأي قسم" hint="أول ما طلب يدخل هيبان هنا" />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ borderRadius: "0 8px 8px 0" }}>القسم</th>
                <th>مفتوح</th>
                <th>متأخر</th>
                <th>مستني استلام</th>
                <th>واقف</th>
                <th>اتقفل النهارده</th>
                <th>متوسط الإقفال</th>
                <th style={{ borderRadius: "8px 0 0 8px" }}>الأعضاء</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.dept} className="row-link">
                  <td className="t-body">
                    {r.dept_ar}
                    {r.has_no_staff ? (
                      <span className="src" style={{ marginRight: 8 }}>
                        مفيهوش حد
                      </span>
                    ) : null}
                  </td>
                  <td>
                    <NumCell n={r.open} onClick={() => onOpen("open", r.dept)} />
                  </td>
                  <td>
                    {r.overdue > 0 ? (
                      <button
                        type="button"
                        className="link"
                        onClick={() => onOpen("overdue", r.dept)}
                      >
                        <span className="chip late">
                          <span className="bullet" />
                          <span className="num">{fmtNum(r.overdue)}</span>
                        </span>
                      </button>
                    ) : (
                      <span className="num">0</span>
                    )}
                  </td>
                  <td>
                    <NumCell n={r.unassigned} onClick={() => onOpen("unassigned", r.dept)} />
                  </td>
                  <td>
                    <NumCell n={r.blocked} onClick={() => onOpen("blocked", r.dept)} />
                  </td>
                  <td>
                    <NumCell n={r.closed_today} onClick={() => onOpen("closed_today", r.dept)} />
                  </td>
                  <td>
                    {/* ⛔ «—» لما العيّنة أصغر من إن المتوسط يبقى له معنى */}
                    {/* ⛔ «—» مش «0» — الصفر ادّعاء إن إحنا قِسنا ولقينا ولا حاجة */}
                    <span className={r.avg_close_hours === null ? "t-desc" : "num"}>
                      {dash(r.avg_close_hours)}
                    </span>
                    {r.avg_close_hours !== null ? (
                      <span className="t-meta"> ساعة</span>
                    ) : null}
                  </td>
                  <td>
                    <span className="num">{fmtNum(r.members)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="t-meta" style={{ borderTop: "1px solid var(--line)", paddingTop: 12, lineHeight: 1.7 }}>
        «متوسط الإقفال» محسوب على آخر <span className="num">{fmtNum(windowDays)}</span> يوم،
        وبيتعرض «—» لو القسم قفل أقل من <span className="num">{fmtNum(minSample)}</span> مهام
        — العيّنة الصغيرة بتدّي رقمًا يوهم بدقّة مش موجودة.
      </div>
    </div>
  );
}

function NumCell({ n, onClick }: { n: number; onClick: () => void }) {
  if (n === 0) return <span className="num">0</span>;
  return (
    <button type="button" className="link" onClick={onClick}>
      <span className="num">{fmtNum(n)}</span>
    </button>
  );
}

function Counter({ label, n, onClick }: { label: string; n: number; onClick: () => void }) {
  return (
    <button
      type="button"
      className="btn sm ghost"
      disabled={n === 0}
      onClick={onClick}
      style={{ gap: 8 }}
    >
      <span className="num">{fmtNum(n)}</span> {label}
    </button>
  );
}

/* ══════════════════ الرحلات الواقفة ══════════════════ */

function StalledJourneys({ value, onOpen }: { value: Overview; onOpen: () => void }) {
  const rows = value.stalled_journeys;
  const hours = value.thresholds.journey_stall_hours;
  return (
    <div className="card" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
      <div className="sec-h">
        <span className="sec-title">
          رحلات واقفة (<span className="num">{fmtNum(rows.length)}</span>)
        </span>
        <span className="sec-note">
          ما اتحركتش من أكتر من <span className="num">{fmtNum(hours)}</span> ساعة
        </span>
        {rows.length > 0 ? (
          <button
            type="button"
            className="btn sm ghost"
            style={{ marginRight: "auto" }}
            onClick={onOpen}
          >
            القايمة كاملة
          </button>
        ) : null}
      </div>

      {rows.length === 0 ? (
        <EmptyState title="مفيش رحلة واقفة" hint="كل الرحلات النشطة اتحركت في وقتها" />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {rows.slice(0, 5).map((j, i) => (
            <div
              key={j.id}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                minWidth: 0,
                ...(i > 0 ? { borderTop: "1px solid var(--line)", paddingTop: 10 } : {}),
              }}
            >
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                <span className="t-body clip">{j.subject_label_ar}</span>
                <span className="chip soon">
                  <span className="bullet" />
                  واقفة من {j.stalled_label_ar}
                </span>
              </div>
              <span className="t-desc clip">
                {j.current_step_no === null
                  ? "بلا خطوة حالية"
                  : `خطوة ${fmtNum(j.current_step_no)} من ${fmtNum(j.steps_total)}`}
                {j.step_title_ar ? ` — ${j.step_title_ar}` : ""}
                {j.owner_dept_ar ? ` · عند ${j.owner_dept_ar}` : ""}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ══════════════════ القايمة ورا الرقم ══════════════════ */

function DrillPanel({
  state,
  page,
  onClose,
  onPage,
}: {
  state: DrillState;
  page: number;
  onClose: () => void;
  onPage: (p: number) => void;
}) {
  if (state.kind === "idle") return null;

  const title =
    state.kind === "ready"
      ? DRILL_TITLES_AR[state.value.kind]
      : DRILL_TITLES_AR[state.drill];

  return (
    <div className="card" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
      <div className="sec-h">
        <span className="sec-title">{title}</span>
        {state.kind === "ready" ? (
          <span className="sec-note">
            <span className="num">{fmtNum(state.value.total)}</span> صف
          </span>
        ) : null}
        <button
          type="button"
          className="btn sm ghost"
          style={{ marginRight: "auto" }}
          onClick={onClose}
        >
          إقفال القايمة
        </button>
      </div>

      {state.kind === "loading" ? <LoadingState rows={3} /> : null}
      {state.kind === "error" ? <ErrorState messageAr={state.messageAr} /> : null}

      {state.kind === "ready" && state.value.rows.length === 0 ? (
        <EmptyState title="القايمة فاضية" hint="الرقم اتغيّر من ساعة ما اللوحة اتحمّلت" />
      ) : null}

      {state.kind === "ready" && state.value.rows.length > 0 ? (
        <>
          <div style={{ overflowX: "auto" }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ borderRadius: "0 8px 8px 0" }}>البند</th>
                  <th>القسم</th>
                  <th style={{ borderRadius: "8px 0 0 8px" }}>الحالة</th>
                </tr>
              </thead>
              <tbody>
                {state.value.rows.map((row, i) => (
                  <tr key={String(row["id"] ?? i)} className="row-link">
                    <td className="t-body">
                      {String(row["title_ar"] ?? row["subject_label_ar"] ?? row["name_ar"] ?? "—")}
                    </td>
                    <td className="t-desc">
                      {String(row["dept_ar"] ?? row["owner_dept_ar"] ?? "—")}
                    </td>
                    <td>
                      <RowStatus row={row} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {state.value.totalPages > 1 ? (
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <span className="t-meta">
                صفحة <span className="num">{fmtNum(page)}</span> من{" "}
                <span className="num">{fmtNum(state.value.totalPages)}</span>
              </span>
              <button
                type="button"
                className="btn sm ghost"
                disabled={page <= 1}
                onClick={() => onPage(Math.max(1, page - 1))}
              >
                السابق
              </button>
              <button
                type="button"
                className="btn sm ghost"
                disabled={page >= state.value.totalPages}
                onClick={() => onPage(Math.min(state.value.totalPages, page + 1))}
              >
                التالي
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/** الحالة = **لون واحد ونقطة واحدة** — ⛔ ممنوع صفّان ملوّنان على نفس الصف */
function RowStatus({ row }: { row: Record<string, unknown> }) {
  if (row["is_overdue"] === true) {
    return (
      <span className="chip late">
        <span className="bullet" />
        متأخرة
      </span>
    );
  }
  const waiting = row["waiting_on_label_ar"];
  if (typeof waiting === "string" && waiting.length > 0) {
    return (
      <span className="chip soon">
        <span className="bullet" />
        {waiting}
      </span>
    );
  }
  const stalled = row["stalled_label_ar"];
  if (typeof stalled === "string" && stalled.length > 0) {
    return (
      <span className="chip soon">
        <span className="bullet" />
        واقفة من {stalled}
      </span>
    );
  }
  if (row["assignee_id"] === null && row["state"] === "open") {
    return (
      <span className="chip ghost">
        <span className="bullet" />
        بلا صاحب
      </span>
    );
  }
  const assignee = row["assignee_name_ar"];
  if (typeof assignee === "string" && assignee.length > 0) {
    return <span className="t-desc">{assignee}</span>;
  }
  const status = row["status"];
  if (typeof status === "string" && status.length > 0) {
    return (
      <span className="chip ghost">
        <span className="bullet" />
        {status === "pending_approval" ? "مستني اعتمادك" : "مستني تأكيد البريد"}
      </span>
    );
  }
  return <span className="t-desc">—</span>;
}
